import os from 'os';
import path from 'path';
import _ from 'lodash';
import isDocker from 'is-docker';
import ciEnv from 'ci-info';
import { env, generateInstallId } from '@strapi/utils';
import type { Core } from '@strapi/types';
import { generateAdminUserHash } from './admin-user-hash';

// Lazy: only resolved when telemetry is enabled and a sender is constructed
let lazyTsUtils: typeof import('@strapi/typescript-utils') | undefined;
const tsUtils = (): typeof import('@strapi/typescript-utils') => {
  if (!lazyTsUtils) {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    lazyTsUtils = require('@strapi/typescript-utils');
  }
  return lazyTsUtils as typeof import('@strapi/typescript-utils');
};

export interface Payload {
  eventProperties?: Record<string, unknown>;
  userProperties?: Record<string, unknown>;
  groupProperties?: Record<string, unknown>;
}

export type Sender = (
  event: string,
  payload?: Payload,
  opts?: Record<string, unknown>
) => Promise<boolean>;

const defaultQueryOpts = {
  timeout: 1000,
  headers: { 'Content-Type': 'application/json' },
};

/**
 * Add properties from the package.json strapi key in the metadata
 */
const addPackageJsonStrapiMetadata = (metadata: Record<string, unknown>, strapi: Core.Strapi) => {
  const { packageJsonStrapi = {} } = strapi.config;

  _.defaults(metadata, packageJsonStrapi);
};

/**
 * Create a send function for event with all the necessary metadata
 */
export default (strapi: Core.Strapi): Sender => {
  const { uuid, installId: installIdFromPackageJson } = strapi.config;

  const installId = generateInstallId(uuid, installIdFromPackageJson);

  const serverRootPath = strapi.dirs.app.root;
  const adminRootPath = path.join(strapi.dirs.app.root, 'src', 'admin');

  const anonymousUserProperties = {
    environment: strapi.config.environment,
    os: os.type(),
    osPlatform: os.platform(),
    osArch: os.arch(),
    osRelease: os.release(),
    nodeVersion: process.versions.node,
  };

  const anonymousGroupProperties = {
    docker: process.env.DOCKER || isDocker(),
    isCI: ciEnv.isCI,
    version: strapi.config.get('info.strapi'),
    useTypescriptOnServer: tsUtils().isUsingTypeScriptSync(serverRootPath),
    useTypescriptOnAdmin: tsUtils().isUsingTypeScriptSync(adminRootPath),
    projectId: uuid,
    isHostedOnStrapiCloud: env('STRAPI_HOSTING', null) === 'strapi.cloud',
  };

  addPackageJsonStrapiMetadata(anonymousGroupProperties, strapi);

  return async (event: string, payload: Payload = {}, opts = {}) => {
    const userId = generateAdminUserHash(strapi);

    const eeGroupProps: Record<string, unknown> = {};
    if (strapi.ee?.subscriptionId !== undefined && strapi.ee?.subscriptionId !== null) {
      eeGroupProps.subscriptionId = strapi.ee.subscriptionId;
    }
    if (strapi.ee?.planPriceId !== undefined && strapi.ee?.planPriceId !== null) {
      eeGroupProps.planPriceId = strapi.ee.planPriceId;
    }

    const reqParams = {
      method: 'POST',
      body: JSON.stringify({
        event,
        userId,
        installId,
        eventProperties: payload.eventProperties,
        userProperties: userId ? { ...anonymousUserProperties, ...payload.userProperties } : {},
        groupProperties: {
          ...anonymousGroupProperties,
          projectType: strapi.EE ? 'Enterprise' : 'Community',
          ...eeGroupProps,
          ...payload.groupProperties,
        },
      }),
      ..._.merge({ headers: { 'X-Strapi-Event': event } }, defaultQueryOpts, opts),
    };

    try {
      const analyticsUrl = env('STRAPI_ANALYTICS_URL', 'https://analytics.strapi.io');
      const res = await strapi.fetch(`${analyticsUrl}/api/v2/track`, reqParams);
      return res.ok;
    } catch (err) {
      return false;
    }
  };
};
